import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { updateDoc, doc, query, collection, where, getDocs, writeBatch, getDoc } from 'firebase/firestore';
import { Button, Chip, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, TextField, Badge, Menu } from '@mui/material';
import { db } from '../../../firebase';
import festivosData from '../../../data/festivosEspana.json';
import { fetchVigilantesPorServicio } from '../../admin/firestoreService';
import { eliminarFilaCompleta, obtenerTurnoPorId, obtenerTurnosPorCuadrante, agregarTurno } from '../data/FirestoreCuadrantes';
import { actualizarCuadranteFreematica } from '../services/ApiMarcajes';
import {
  aplicarCambioLocal,
  aplicarCambiosLocales,
  agruparCambios,
  contarCambiosPendientes,
  validarCambio,
  crearCambioAsignacion,
  crearCambioEliminacion,
  crearCambioMovimiento,
  aplicarCambioMovimiento
} from '../data/CuadranteChangesManager';
import {
  formatearVigilante,
  eventoSobreFila,
  calcularTotalHoras as calcularTotalHorasHelper,
  calcularHorasDisponibles as calcularHorasDisponiblesHelper,
  calcularTotalHorasPorDia as calcularTotalHorasPorDiaHelper,
  calcularHorasTotales,
  generarDiasDelMes,
  tieneTurno as tieneTurnoHelper,
  obtenerVigilantesUnicos
} from './GridViewHelpers';
import profiler from '../utils/PerformanceProfiler';
import ActualizarTurno from '../modals/Actualizarturno';
import AsignarVigilante from '../modals/AsignarVigilante';
import CreateTramo from '../modals/CreateTramo';
import './GridView.css';

const GridView = ({ events, cuadranteId, currentService, servicioId, onMesChange, onTurnoActualizado, mesInicial, tiposFestivosOcultar = [] }) => {
  const [mostrarFormularioTramo, setMostrarFormularioTramo] = useState(false);
  const [filaAEliminar, setFilaAEliminar] = useState(null);
  const [mesSeleccionado, setMesSeleccionado] = useState(mesInicial || new Date());
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [processedEvents, setProcessedEvents] = useState([]);
  const [turnosDetalle, setTurnosDetalle] = useState({});
  const [mostrarAsignarVigilante, setMostrarAsignarVigilante] = useState(false);
  const [turnoParaAsignar, setTurnoParaAsignar] = useState(null);
  /** Cuando se asigna desde una fila por patrón (L-V 8-16), se asignan todos los turnos de la fila */
  const [asignarFilaCompleta, setAsignarFilaCompleta] = useState(null);
  const [cargandoTurnos, setCargandoTurnos] = useState(false);
  /** Cuando en una celda hay varios turnos (líneas duplicadas), el usuario elige cuál actualizar */
  const [selectorTurnoAbierto, setSelectorTurnoAbierto] = useState(false);
  const [eventosParaElegir, setEventosParaElegir] = useState([]);
  
  // Estados para información del cuadrante
  const [infoCuadrante, setInfoCuadrante] = useState({
    empresa: null,
    delegacion: null,
    contrato: null,
    servicio: null
  });
  
  // Nuevos estados para asignación de tramos completos
  const [mostrarAsignarTramoCompleto, setMostrarAsignarTramoCompleto] = useState(false);
  const [vigilantes, setVigilantes] = useState([]);
  const [vigilanteSeleccionado, setVigilanteSeleccionado] = useState('');
  const [loadingVigilantes, setLoadingVigilantes] = useState(false);
  const [asignandoTramo, setAsignandoTramo] = useState(false);
  const [errorAsignacion, setErrorAsignacion] = useState('');
  const [busquedaVigilante, setBusquedaVigilante] = useState('');
  
  // Estados para agregar vigilante sin turnos
  const [mostrarAgregarVigilante, setMostrarAgregarVigilante] = useState(false);
  const [vigilantesSinTurnos, setVigilantesSinTurnos] = useState([]);
  
  // Estados para cambiar usuario de toda la fila (sin eliminar turnos)
  const [mostrarCambiarUsuarioFila, setMostrarCambiarUsuarioFila] = useState(false);
  const [filaParaCambiarUsuario, setFilaParaCambiarUsuario] = useState(null);
  const [busquedaCambiarUsuarioFila, setBusquedaCambiarUsuarioFila] = useState('');
  const [vigilanteSeleccionadoCambiarFila, setVigilanteSeleccionadoCambiarFila] = useState('');
  const [aplicandoCambioUsuarioFila, setAplicandoCambioUsuarioFila] = useState(false);
  
  // Estados para manejo de cambios pendientes
  const [cambiosPendientes, setCambiosPendientes] = useState([]);
  const [guardandoCambios, setGuardandoCambios] = useState(false);
  const [resultadoGuardado, setResultadoGuardado] = useState(null);
  
  // Estados para drag and drop
  const [draggedTurno, setDraggedTurno] = useState(null);
  const [dragOverCell, setDragOverCell] = useState(null);
  
  // Estado para zoom de la tabla
  const [zoomLevel, setZoomLevel] = useState(100);

  // Copiar/pegar turno (menú contexto y Ctrl+C / Ctrl+V)
  const [contextMenuCelda, setContextMenuCelda] = useState(null);
  const [copiedTurnoData, setCopiedTurnoData] = useState(null);
  const [celdaSeleccionada, setCeldaSeleccionada] = useState(null);
  const [pegandoTurno, setPegandoTurno] = useState(false);
  const [duplicandoFila, setDuplicandoFila] = useState(false);

  // Actualizar mes seleccionado cuando mesInicial cambie
  useEffect(() => {
    if (mesInicial) {
      console.log(`📅 GridView: Actualizando mes inicial a`, mesInicial);
      setMesSeleccionado(mesInicial);
    }
  }, [mesInicial]);

  // Obtener información del cuadrante (empresa, delegación, contrato, servicio)
  useEffect(() => {
    const obtenerInfoCuadrante = async () => {
      if (!cuadranteId || !currentService) return;
      
      // 🔍 DETECTAR: ¿Ya hay una sesión activa? (por ejemplo, de creación de cuadrante)
      const haySessionActiva = profiler.currentSession !== null;
      
      // 📊 PROFILING: Medir obtención de info del cuadrante
      const infoId = profiler.startMeasurement('01_ObtenerInfoCuadrante', {
        cuadranteId,
        servicioId: currentService.id
      });
      
      try {
        const cuadranteRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId);
        const cuadranteDoc = await getDoc(cuadranteRef);
        
        if (cuadranteDoc.exists()) {
          const data = cuadranteDoc.data();
          
          // 🔍 DETECTAR: ¿Es un cuadrante recién creado?
          const esCuadranteNuevo = data.createdAt && 
            (new Date() - data.createdAt.toDate()) < 60000; // Creado hace menos de 1 minuto
          
          // 📊 PROFILING: Solo iniciar sesión si NO hay una activa
          if (!haySessionActiva) {
            const sessionId = `cargar_cuadrante_${cuadranteId}_${Date.now()}`;
            profiler.startSession(sessionId, {
              cuadranteId,
              servicioId: currentService.id,
              esCuadranteNuevo: esCuadranteNuevo,
              createdAt: data.createdAt?.toDate()
            });
            
            // Forzar que se detecte como primera carga si es un cuadrante nuevo
            if (esCuadranteNuevo) {
              profiler.isFirstLoad = true;
            }
          }
          
          setInfoCuadrante({
            empresa: data._freematica_empresa || '0001',
            delegacion: data._freematica_delegacion || '06',
            contrato: data._freematica_contrato || '',
            servicio: data._freematica_servicio || ''
          });
          profiler.endMeasurement(infoId, { success: true, esCuadranteNuevo });
        } else {
          // Cuadrante no existe - solo iniciar sesión si no hay una activa
          if (!haySessionActiva) {
            const sessionId = `cargar_cuadrante_${cuadranteId}_${Date.now()}`;
            profiler.startSession(sessionId, {
              cuadranteId,
              servicioId: currentService.id,
              esCuadranteNuevo: true
            });
            profiler.isFirstLoad = true;
          }
          
          profiler.endMeasurement(infoId, { success: false, reason: 'no_existe' });
        }
      } catch (error) {
        console.error('Error obteniendo información del cuadrante:', error);
        profiler.recordError(infoId, error);
      }
    };
    
    obtenerInfoCuadrante();
  }, [cuadranteId, currentService]);

  // Procesar eventos del calendario y obtener detalles adicionales
  useEffect(() => {
    const fetchTurnosDetails = async () => {
      if (!events || !Array.isArray(events)) {
        setCargandoTurnos(false);
        return;
      }

      // 🔍 Si events está vacío Y hay una sesión de creación activa, esperar
      // (no cerrar la sesión prematuramente)
      if (events.length === 0 && profiler.currentSession && 
          profiler.currentSession.sessionId.startsWith('CrearCuadrante_')) {
        console.log('⏸️ GridView: Esperando a que se complete la creación del cuadrante...');
        setCargandoTurnos(false);
        return;
      }

      // 📊 PROFILING: Medir obtención y procesamiento de turnos
      const turnosId = profiler.startMeasurement('02_ObtenerYProcesarTurnos', {
        eventosCount: events.length
      });

      try {
        setCargandoTurnos(true);

        // 🚀 OPTIMIZACIÓN: Obtener todos los turnos en PARALELO en lugar de secuencial
        console.log(`🔄 Obteniendo ${events.length} turnos en paralelo...`);
        
        const turnosPromises = events.map(event => 
          obtenerTurnoPorId(cuadranteId, event.id, currentService)
            .then(turnoDetalle => ({ event, turnoDetalle, error: null }))
            .catch(error => ({ event, turnoDetalle: null, error }))
        );

        const resultados = await Promise.all(turnosPromises);

        const detallesPorId = {};
        const eventosSinTurno = [];

        resultados.forEach(({ event, turnoDetalle, error }) => {
          if (error) {
            console.error('Error obteniendo detalles del turno:', error);
            eventosSinTurno.push(event.id);
          } else if (turnoDetalle) {
            detallesPorId[event.id] = turnoDetalle;
          } else {
            // ⚠️ El turno no existe (fue eliminado)
            console.warn(`⚠️ Turno ${event.id} no existe, será filtrado del calendario`);
            eventosSinTurno.push(event.id);
          }
        });

        console.log(`✅ Obtenidos ${Object.keys(detallesPorId).length} turnos exitosamente`);

        // Los eventos sin turno se filtran automáticamente en el map
        if (eventosSinTurno.length > 0) {
          console.log(`🧹 ${eventosSinTurno.length} turnos eliminados serán omitidos del calendario`);
        }

        setTurnosDetalle(detallesPorId);

        // 📊 PROFILING: Medir procesamiento de eventos
        const processingId = profiler.startMeasurement('03_ProcesarEventos', {
          eventosValidosCount: Object.keys(detallesPorId).length
        });

        // Solo procesar eventos con turno válido
        const processed = events
          .filter(event => detallesPorId[event.id])
          .map(event => {
          const turnoDetalle = detallesPorId[event.id];
          const isSinAsignar = turnoDetalle?.sinAsignar || !turnoDetalle?.vigilante || turnoDetalle?.vigilante === '' || turnoDetalle?.vigilante === null || turnoDetalle?.vigilante === undefined;
          
          // Formatear fecha sin usar toISOString() para evitar problemas de zona horaria
          const formatearFecha = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          
          // Construir title correcto para evitar duplicados
          let title;
          if (isSinAsignar) {
            title = 'Sin Asignar';
          } else {
            const nombreCompleto = `${turnoDetalle?.nombrePersona || ''} ${turnoDetalle?.apellido1 || ''} ${turnoDetalle?.apellido2 || ''}`.trim();
            title = nombreCompleto || turnoDetalle?.vigilante || event.title || 'Sin nombre';
          }
          
          return {
            ...event,
            title: title, // ✅ ESTABLECER TITLE CORRECTAMENTE
            fecha: turnoDetalle?.fecha || formatearFecha(event.start),
            inicioTurno: turnoDetalle?.inicioTurno || (event.start.getHours().toString().padStart(2, '0') + ':' + 
                         event.start.getMinutes().toString().padStart(2, '0')),
            finTurno: turnoDetalle?.finTurno || (event.end.getHours().toString().padStart(2, '0') + ':' + 
                      event.end.getMinutes().toString().padStart(2, '0')),
            turno: turnoDetalle?.turno || '',
            vigilante: turnoDetalle?.vigilante || '',
            nombrePersona: turnoDetalle?.nombrePersona || '',
            apellido1: turnoDetalle?.apellido1 || '',
            apellido2: turnoDetalle?.apellido2 || '',
            sinAsignar: isSinAsignar,
          };
        });

        console.log(`📅 GridView: Procesados ${processed.length} eventos`);
        if (processed.length > 0) {
          const fechas = processed.map(e => e.fecha).filter((v, i, a) => a.indexOf(v) === i).sort();
          console.log(`📆 GridView: Fechas disponibles (${fechas.length} días): ${fechas[0]} a ${fechas[fechas.length - 1]}`);
        }

        profiler.endMeasurement(processingId, {
          procesadosCount: processed.length
        });

        setProcessedEvents(processed);

        profiler.endMeasurement(turnosId, {
          success: true,
          procesadosCount: processed.length
        });

        // 📊 PROFILING: Finalizar sesión si hay una activa
        // Esto incluye sesiones de creación de cuadrantes de DataCuadrates
        if (profiler.currentSession) {
          profiler.endSession();
        }

      } catch (error) {
        console.error('Error cargando turnos:', error);
        profiler.recordError(turnosId, error);
        // Finalizar sesión si hay una activa
        if (profiler.currentSession) {
          profiler.endSession();
        }
      } finally {
        setCargandoTurnos(false);
      }
    };

    fetchTurnosDetails();
  }, [events, cuadranteId, currentService]);

  // Cargar vigilantes cuando se abre el modal de asignación de tramo completo, agregar vigilante o cambiar usuario de fila
  useEffect(() => {
    const cargarVigilantes = async () => {
      if ((!mostrarAsignarTramoCompleto && !mostrarAgregarVigilante && !mostrarCambiarUsuarioFila) || !currentService) return;

      try {
        setLoadingVigilantes(true);
        setErrorAsignacion('');

        // Obtener TODOS los vigilantes del servicio (no solo los del servicio específico)
        let vigilantesData = await fetchVigilantesPorServicio(currentService);

        if (vigilantesData && vigilantesData.length > 0) {
          // Validar y formatear vigilantes
          const vigilantesValidados = vigilantesData.map(v => {
            if (!v.persona && (v.nombre || v.apellidos)) {
              return {
                ...v,
                persona: `${v.nombre || ''} ${v.apellidos || ''}`.trim()
              };
            }
            return v;
          });

          setVigilantes(vigilantesValidados);
        } else {
          setErrorAsignacion('No se encontraron vigilantes disponibles');
        }
      } catch (error) {
        console.error('Error cargando vigilantes:', error);
        setErrorAsignacion('Error al cargar vigilantes: ' + error.message);
      } finally {
        setLoadingVigilantes(false);
      }
    };

    cargarVigilantes();
  }, [mostrarAsignarTramoCompleto, mostrarAgregarVigilante, mostrarCambiarUsuarioFila, currentService]);

  // Obtener usuarios únicos con sus datos (incluyendo turnos sin asignar)
  const uniqueVigilantes = obtenerVigilantesUnicos(processedEvents, vigilantesSinTurnos);

  // Función para agregar vigilante sin turnos
  const handleAgregarVigilanteSinTurnos = () => {
    if (!vigilanteSeleccionado) {
      setErrorAsignacion('Debe seleccionar un vigilante');
      return;
    }

    try {
      const vigilante = vigilantes.find(v => v.id === vigilanteSeleccionado);
      if (!vigilante) {
        setErrorAsignacion('Vigilante no encontrado');
        return;
      }

      const nombreVigilante = formatearVigilante(vigilante);
      
      // Verificar si ya existe
      const yaExiste = vigilantesSinTurnos.some(v => v.nombre === nombreVigilante) ||
                      uniqueVigilantes.some(v => v.nombre === nombreVigilante);
      
      if (yaExiste) {
        setErrorAsignacion('Este vigilante ya está en la lista');
        return;
      }

      // Agregar al estado
      setVigilantesSinTurnos([...vigilantesSinTurnos, {
        nombre: nombreVigilante,
        persona: vigilante.persona || '',
        id: vigilante.id
      }]);

      // Cerrar modal y limpiar
      setMostrarAgregarVigilante(false);
      setVigilanteSeleccionado('');
      setBusquedaVigilante('');
      setErrorAsignacion('');
    } catch (error) {
      console.error('Error al agregar vigilante:', error);
      setErrorAsignacion('Error al agregar vigilante: ' + error.message);
    }
  };

  // Función para obtener todos los turnos sin asignar del mes actual
  const obtenerTurnosSinAsignar = () => {
    console.log(`🔍 GridView: Filtrando eventos para mes ${mesSeleccionado.getMonth() + 1}/${mesSeleccionado.getFullYear()}`);
    const eventosSinAsignar = processedEvents.filter(event => {
      if (event._pendienteEliminar) return false;
      const eventDate = new Date(event.start);
      const isSameMonth = eventDate.getFullYear() === mesSeleccionado.getFullYear() &&
                         eventDate.getMonth() === mesSeleccionado.getMonth();
      
      return event.sinAsignar && isSameMonth;
    });
    
    console.log(`📊 GridView: ${eventosSinAsignar.length} eventos sin asignar encontrados de ${processedEvents.length} eventos totales`);
    return eventosSinAsignar;
  };

  // Función para asignar vigilante a todo el tramo (todos los turnos sin asignar del mes)
  const handleAsignarTramoCompleto = async () => {
    if (!vigilanteSeleccionado) {
      setErrorAsignacion('Debe seleccionar un vigilante');
      return;
    }

    try {
      setAsignandoTramo(true);
      setErrorAsignacion('');

      // Buscar el vigilante seleccionado
      const vigilante = vigilantes.find(v => v.id === vigilanteSeleccionado);

      if (!vigilante) {
        throw new Error('Vigilante no encontrado');
      }

      // Verificar que tiene código de persona válido
      if (!vigilante.persona || vigilante.persona === 'undefined' || vigilante.persona === 'null') {
        throw new Error('El vigilante seleccionado no tiene un código de persona válido');
      }

      // Obtener todos los turnos sin asignar del mes actual
      const turnosSinAsignar = obtenerTurnosSinAsignar();
      
      if (turnosSinAsignar.length === 0) {
        throw new Error('No hay turnos sin asignar en el mes actual');
      }

      // Crear cambios para todos los turnos sin asignar
      const nuevosCambios = turnosSinAsignar.map(turno => 
        crearCambioAsignacion(turno.id, {
          persona: vigilante.persona,
          id: vigilanteSeleccionado,
          nombreCompleto: formatearVigilante(vigilante),
          esExterno: vigilante.esExterno || false,
          servicioPrincipal: vigilante.esExterno ? vigilante.servicioPrincipal || null : null
        })
      );

      // Agregar a cambios pendientes
      setCambiosPendientes([...cambiosPendientes, ...nuevosCambios]);

      // Aplicar cambios localmente para visualización inmediata
      const eventosActualizados = aplicarCambiosLocales(processedEvents, nuevosCambios);
      setProcessedEvents(eventosActualizados);

      setMostrarAsignarTramoCompleto(false);
      setVigilanteSeleccionado('');
      setErrorAsignacion('');
      setBusquedaVigilante('');

    } catch (error) {
      console.error('Error al asignar tramo completo:', error);
      setErrorAsignacion('Error al asignar tramo completo: ' + error.message);
    } finally {
      setAsignandoTramo(false);
    }
  };

  
  const obtenerHoraInicioFin = (vigilante, dia) => {
    const diaFormateado = dia.toString().padStart(2, '0');
    const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
    const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;

    const event = processedEvents.find(event => {
      if (vigilante === 'Sin Asignar') {
        return event.sinAsignar && event.fecha === fechaBuscada;
      }
      return event.title === vigilante && event.fecha === fechaBuscada;
    });

    return event ? `${event.inicioTurno}-${event.finTurno}` : '';
  };

  // Wrappers para funciones auxiliares
  const calcularTotalHoras = (vigilante) => {
    return calcularTotalHorasHelper(vigilante, processedEvents, mesSeleccionado);
  };

  const calcularHorasDisponibles = (vigilante) => {
    return calcularHorasDisponiblesHelper(vigilante, processedEvents, mesSeleccionado);
  };

  const calcularTotalHorasPorDia = (dia) => {
    return calcularTotalHorasPorDiaHelper(dia, processedEvents, mesSeleccionado);
  };

  // Función para determinar si un día debe ser ocultado según los filtros seleccionados
  const debeOcultarDia = (dia) => {
    if (tiposFestivosOcultar.length === 0) return false;

    const año = mesSeleccionado.getFullYear();
    const mes = mesSeleccionado.getMonth() + 1;
    const fecha = new Date(año, mes - 1, dia);
    const diaSemana = fecha.getDay(); // 0 = Domingo, 6 = Sábado

    // Verificar cada tipo seleccionado
    for (const tipo of tiposFestivosOcultar) {
      if (tipo === 'nacionales') {
        const festivosDelAño = festivosData.festivosNacionales[año] || [];
        const fechaStr = `${año}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
        if (festivosDelAño.includes(fechaStr)) return true;
      }
      
      if (tipo === 'domingos' && diaSemana === 0) {
        return true;
      }
      
      if (tipo === 'sabados' && diaSemana === 6) {
        return true;
      }
    }

    return false;
  };

  const tieneTurno = (vigilante, dia) => {
    return tieneTurnoHelper(vigilante, dia, processedEvents, mesSeleccionado);
  };

  // Resto de funciones auxiliares
  const handleMostrarFormularioTramo = () => {
    setMostrarFormularioTramo(true);
  };

  const handleEliminarFila = (vigilante) => {
    const nombre = typeof vigilante === 'object' ? vigilante?.nombre : vigilante;
    const vigilanteSinTurnos = vigilantesSinTurnos.find(v => v.nombre === nombre);
    if (vigilanteSinTurnos) {
      setVigilantesSinTurnos(vigilantesSinTurnos.filter(v => v.nombre !== nombre));
      return;
    }
    let eventosAEliminar;
    if (typeof vigilante === 'object' && vigilante?.sinAsignar) {
      if (Array.isArray(vigilante.eventIds) && vigilante.eventIds.length > 0) {
        eventosAEliminar = [...vigilante.eventIds];
      } else if (vigilante.eventId != null) {
        eventosAEliminar = [vigilante.eventId];
      } else {
        eventosAEliminar = processedEvents.filter(event => event.sinAsignar).map(event => event.id);
      }
    } else if (nombre === 'Sin Asignar') {
      eventosAEliminar = processedEvents.filter(event => event.sinAsignar).map(event => event.id);
    } else {
      eventosAEliminar = processedEvents
        .filter(event => eventoSobreFila(event, vigilante))
        .map(event => event.id);
    }
    setFilaAEliminar(eventosAEliminar);
  };

  const handleConfirmarEliminarFila = async () => {
    try {
      for (const eventId of filaAEliminar) {
        await eliminarFilaCompleta(eventId, currentService, cuadranteId);
      }
      setFilaAEliminar(null);
      if (onTurnoActualizado) {
        await onTurnoActualizado();
      }
    } catch (error) {
      console.error('Error al eliminar la fila:', error);
      alert('Error al eliminar la fila: ' + (error?.message || error));
    }
  };

  const handleCambiarUsuarioFila = (vigilante) => {
    const nombre = typeof vigilante === 'object' ? vigilante?.nombre : vigilante;
    setFilaParaCambiarUsuario({ vigilanteNombre: nombre, vigilanteRow: typeof vigilante === 'object' ? vigilante : null });
    setMostrarCambiarUsuarioFila(true);
    setBusquedaCambiarUsuarioFila('');
    setVigilanteSeleccionadoCambiarFila('');
  };

  const handleConfirmarCambiarUsuarioFila = async () => {
    if (!filaParaCambiarUsuario || !vigilanteSeleccionadoCambiarFila || !currentService?.id || !cuadranteId) return;
    const vigilante = vigilantes.find(v => v.id === vigilanteSeleccionadoCambiarFila);
    if (!vigilante?.persona) {
      alert('Seleccione un usuario con código de persona válido');
      return;
    }
    const eventosDeLaFila = filaParaCambiarUsuario.vigilanteRow
      ? processedEvents.filter(e => eventoSobreFila(e, filaParaCambiarUsuario.vigilanteRow))
      : processedEvents.filter(
          e => e.title === filaParaCambiarUsuario.vigilanteNombre || e.vigilante === filaParaCambiarUsuario.vigilanteNombre || e.nombre === filaParaCambiarUsuario.vigilanteNombre
        );
    const eventIds = eventosDeLaFila.map(e => e.id);
    if (eventIds.length === 0) {
      alert('No hay turnos en esta fila');
      return;
    }
    try {
      setAplicandoCambioUsuarioFila(true);
      const turnoRef = (id) => doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId, 'turnos', id);
      const updateData = {
        vigilante: vigilante.persona,
        vigilanteId: vigilante.id,
        personaId: vigilante.persona,
        nombrePersona: vigilante.nombre || '',
        apellido1: vigilante.apellidos || '',
        apellido2: '',
        sinAsignar: false,
        fechaActualizacion: new Date().toISOString()
      };
      for (const turnoId of eventIds) {
        await updateDoc(turnoRef(turnoId), updateData);
      }
      setMostrarCambiarUsuarioFila(false);
      setFilaParaCambiarUsuario(null);
      setVigilanteSeleccionadoCambiarFila('');
      setBusquedaCambiarUsuarioFila('');
      if (onTurnoActualizado) await onTurnoActualizado();
    } catch (error) {
      console.error('Error al cambiar usuario de la fila:', error);
      alert('Error al cambiar usuario: ' + (error?.message || error));
    } finally {
      setAplicandoCambioUsuarioFila(false);
    }
  };

  const handleMesChange = (e) => {
    const nuevaFecha = new Date(e.target.value);
    setMesSeleccionado(nuevaFecha);
    if (onMesChange) {
      onMesChange(nuevaFecha);
    }
  };

  // Generar días del mes usando helper
  const daysOfMonth = generarDiasDelMes(mesSeleccionado);

  const getEventosEnCelda = (vigilante, diaNum) => {
    const diaFormateado = diaNum.toString().padStart(2, '0');
    const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
    const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;
    return processedEvents.filter((e) => !e._pendienteEliminar && e.fecha === fechaBuscada && eventoSobreFila(e, vigilante));
  };

  const handleContextMenuCelda = (e, vigilante, diaNum) => {
    e.preventDefault();
    setCeldaSeleccionada({ vigilante, dia: diaNum });
    setContextMenuCelda({
      mouseX: e.clientX,
      mouseY: e.clientY,
      vigilante,
      dia: diaNum
    });
  };

  const handleCopiarTurno = () => {
    if (!contextMenuCelda) return;
    const eventos = getEventosEnCelda(contextMenuCelda.vigilante, contextMenuCelda.dia);
    const primerEvento = eventos[0];
    if (primerEvento) {
      setCopiedTurnoData({
        inicioTurno: primerEvento.inicioTurno || '08:00',
        finTurno: primerEvento.finTurno || '16:00',
        turno: primerEvento.turno || ''
      });
    }
    setContextMenuCelda(null);
  };

  const handlePegarTurno = async () => {
    if (!contextMenuCelda || !copiedTurnoData || !cuadranteId || !currentService) {
      setContextMenuCelda(null);
      return;
    }
    const diaFormateado = contextMenuCelda.dia.toString().padStart(2, '0');
    const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
    const fecha = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;
    setPegandoTurno(true);
    try {
      await agregarTurno(
        cuadranteId,
        {
          fecha,
          inicioTurno: copiedTurnoData.inicioTurno,
          finTurno: copiedTurnoData.finTurno,
          turno: copiedTurnoData.turno,
          sinAsignar: true
        },
        currentService
      );
      if (onTurnoActualizado) await onTurnoActualizado();
    } catch (err) {
      console.error('Error al pegar turno:', err);
    } finally {
      setPegandoTurno(false);
      setContextMenuCelda(null);
    }
  };

  const handleDuplicarFila = async (vigilante) => {
    const eventosFila = processedEvents.filter((e) => !e._pendienteEliminar && eventoSobreFila(e, vigilante));
    if (!eventosFila.length || !cuadranteId || !currentService) return;
    setDuplicandoFila(true);
    try {
      for (const event of eventosFila) {
        await agregarTurno(
          cuadranteId,
          {
            fecha: event.fecha,
            inicioTurno: event.inicioTurno || '08:00',
            finTurno: event.finTurno || '16:00',
            turno: event.turno || '',
            sinAsignar: true
          },
          currentService
        );
      }
      if (onTurnoActualizado) await onTurnoActualizado();
    } catch (err) {
      console.error('Error al duplicar fila:', err);
    } finally {
      setDuplicandoFila(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!celdaSeleccionada) return;
      if (e.ctrlKey && e.key === 'c') {
        const eventos = getEventosEnCelda(celdaSeleccionada.vigilante, celdaSeleccionada.dia);
        const primerEvento = eventos[0];
        if (primerEvento) {
          setCopiedTurnoData({
            inicioTurno: primerEvento.inicioTurno || '08:00',
            finTurno: primerEvento.finTurno || '16:00',
            turno: primerEvento.turno || ''
          });
        }
      }
      if (e.ctrlKey && e.key === 'v') {
        if (copiedTurnoData && cuadranteId && currentService) {
          const diaFormateado = celdaSeleccionada.dia.toString().padStart(2, '0');
          const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
          const fecha = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;
          setPegandoTurno(true);
          agregarTurno(
            cuadranteId,
            {
              fecha,
              inicioTurno: copiedTurnoData.inicioTurno,
              finTurno: copiedTurnoData.finTurno,
              turno: copiedTurnoData.turno,
              sinAsignar: true
            },
            currentService
          ).then(() => onTurnoActualizado?.()).finally(() => setPegandoTurno(false));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [celdaSeleccionada, copiedTurnoData, cuadranteId, currentService, mesSeleccionado, onTurnoActualizado]);

  const handleEditarCasilla = (vigilante, dia) => {
    const diaFormateado = dia.toString().padStart(2, '0');
    const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
    const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;


    // Buscar TODOS los turnos del día para este vigilante (vigilante puede ser objeto con eventId para filas sin asignar)
    const eventosDelDia = processedEvents.filter(event => {
      if (event._pendienteEliminar) return false;
      if (event.fecha !== fechaBuscada) return false;
      return eventoSobreFila(event, vigilante);
    });


    if (eventosDelDia.length === 0) {
      return;
    }

    // Si hay múltiples turnos (líneas duplicadas), mostrar selector para que el usuario elija cuál editar
    if (eventosDelDia.length > 1) {
      const turnosSinAsignar = eventosDelDia.filter(e => e.sinAsignar);
      const turnosAsignados = eventosDelDia.filter(e => !e.sinAsignar);
      if (turnosAsignados.length === 0) {
        setTurnoParaAsignar(turnosSinAsignar[0]);
        setMostrarAsignarVigilante(true);
      } else {
        setEventosParaElegir(eventosDelDia);
        setSelectorTurnoAbierto(true);
      }
    } else {
      // Si solo hay un turno, manejarlo como antes
      const evento = eventosDelDia[0];
      
      if (evento.sinAsignar) {
        setTurnoParaAsignar(evento);
        setMostrarAsignarVigilante(true);
      } else {
        setTurnoSeleccionado(evento.id);
      }
    }
  };

  // Función para manejar la asignación de vigilante (flujo antiguo - deprecated)
  const handleVigilanteAsignado = async () => {
    // Recargar los datos después de asignar un vigilante
    if (onTurnoActualizado) {
      await onTurnoActualizado();
    }
    setMostrarAsignarVigilante(false);
    setTurnoParaAsignar(null);
    setAsignarFilaCompleta(null);
  };

  // 🆕 NUEVA FUNCIÓN: Agregar asignación a cambios pendientes
  const aplicarUnCambioPendiente = (cambioPendiente, turnoActual, turnosDetallePrev, processedEventsPrev) => {
    const turnoActualizado = {
      ...turnoActual,
      vigilante: cambioPendiente.vigilanteNuevo.persona,
      vigilanteId: cambioPendiente.vigilanteNuevo.id,
      nombrePersona: cambioPendiente.vigilanteNuevo.nombre,
      apellido1: cambioPendiente.vigilanteNuevo.apellidos.split(' ')[0] || '',
      apellido2: cambioPendiente.vigilanteNuevo.apellidos.split(' ').slice(1).join(' ') || '',
      sinAsignar: false,
      esVigilanteExterno: cambioPendiente.vigilanteNuevo.esExterno || false,
      servicioPrincipal: cambioPendiente.vigilanteNuevo.servicioPrincipal || null,
      _pendienteGuardar: true
    };
    const nombreCompleto = `${turnoActualizado.nombrePersona || ''} ${turnoActualizado.apellido1 || ''} ${turnoActualizado.apellido2 || ''}`.trim();
    const tituloVigilante = nombreCompleto || turnoActualizado.vigilante || 'Sin nombre';
    return {
      turnosDetalle: { ...turnosDetallePrev, [cambioPendiente.turnoId]: turnoActualizado },
      processedEvents: processedEventsPrev.map(event =>
        event.id === cambioPendiente.turnoId
          ? { ...event, title: tituloVigilante, vigilante: turnoActualizado.vigilante, nombrePersona: turnoActualizado.nombrePersona, apellido1: turnoActualizado.apellido1, apellido2: turnoActualizado.apellido2, sinAsignar: false, _pendienteGuardar: true }
          : event
      )
    };
  };

  const handleAsignacionPendiente = async (cambioPendiente) => {
    try {
      console.log('📝 Recibido cambio pendiente:', cambioPendiente);
      const turnoIdsAProcesar = asignarFilaCompleta?.eventIds?.length
        ? asignarFilaCompleta.eventIds
        : [cambioPendiente.turnoId];

      const cambiosCompletos = [];
      let turnosDetalleNext = { ...turnosDetalle };
      let processedEventsNext = [...processedEvents];

      for (const turnoId of turnoIdsAProcesar) {
        const turnoActual = await obtenerTurnoPorId(cuadranteId, turnoId, currentService);
        if (!turnoActual) continue;
        const cambioParaTurno = { ...cambioPendiente, turnoId };
        const cambioCompleto = {
          ...cambioParaTurno,
          vigilanteAnterior: turnoActual ? { id: turnoActual.vigilanteId, persona: turnoActual.vigilante, nombre: turnoActual.nombrePersona || '', apellidos: (turnoActual.apellido1 || '') + ' ' + (turnoActual.apellido2 || ''), esExterno: turnoActual.esVigilanteExterno || false } : null,
          turnoData: { fecha: turnoActual?.fecha, inicioTurno: turnoActual?.inicioTurno, finTurno: turnoActual?.finTurno, numeroTurno: turnoActual?.numeroTurno }
        };
        cambiosCompletos.push(cambioCompleto);
        const { turnosDetalle: td, processedEvents: pe } = aplicarUnCambioPendiente(cambioParaTurno, turnoActual, turnosDetalleNext, processedEventsNext);
        turnosDetalleNext = td;
        processedEventsNext = pe;
      }

      setCambiosPendientes(prev => [...prev, ...cambiosCompletos]);
      setTurnosDetalle(turnosDetalleNext);
      setProcessedEvents(processedEventsNext);
      setMostrarAsignarVigilante(false);
      setTurnoParaAsignar(null);
      setAsignarFilaCompleta(null);
      console.log('✅ Cambios agregados a pendientes:', cambiosCompletos.length);
    } catch (error) {
      console.error('❌ Error al agregar cambio pendiente:', error);
    }
  };

  // Función para eliminar un turno individual
  const handleEliminarTurno = async (event) => {
    try {
      if (!event || !event.id) {
        console.error('❌ Evento inválido para eliminar');
        return;
      }

      console.log('🗑️ Eliminando turno:', event.id);

      // Confirmar eliminación
      if (!window.confirm(`¿Estás seguro de eliminar el turno del ${event.fecha}?`)) {
        return;
      }

      // Crear cambio pendiente de eliminación
      const cambioEliminacion = {
        tipo: 'eliminar',
        turnoId: event.id,
        turnoData: {
          fecha: event.fecha,
          inicioTurno: event.inicioTurno,
          finTurno: event.finTurno,
          vigilante: event.vigilante
        }
      };

      // Agregar a cambios pendientes
      setCambiosPendientes(prev => [...prev, cambioEliminacion]);

      // Actualizar el estado local para reflejar la eliminación visualmente
      setTurnosDetalle(prev => {
        const updated = { ...prev };
        if (updated[event.id]) {
          updated[event.id] = {
            ...updated[event.id],
            vigilante: '',
            vigilanteId: null,
            sinAsignar: true,
            _pendienteEliminar: true
          };
        }
        return updated;
      });

      // Actualizar processedEvents
      setProcessedEvents(prev => prev.map(ev => 
        ev.id === event.id
          ? { 
              ...ev,
              title: 'Sin Asignar',
              vigilante: '',
              sinAsignar: true,
              _pendienteEliminar: true
            }
          : ev
      ));

      console.log('✅ Turno marcado para eliminación');
    } catch (error) {
      console.error('❌ Error al eliminar turno:', error);
      alert('Error al eliminar el turno: ' + error.message);
    }
  };

  // Nueva función para asignar vigilante a Freematica
  const handleAsignarVigilanteAFreematica = async (turnoId, vigilanteId, vigilanteData) => {
    try {
      // Obtener detalles del turno
      const turnoDetalle = await obtenerTurnoPorId(cuadranteId, turnoId, currentService);
      if (!turnoDetalle) {
        throw new Error('No se pudo obtener los detalles del turno');
      }

      // Extraer información del servicio para construir el cuadrante
      const servicioInfo = currentService;
      
      // Extraer contrato y servicio de la localización
      let contrato, servicio;
      if (servicioInfo.localizacion) {
        const partes = servicioInfo.localizacion.split('/');
        if (partes.length >= 2) {
          contrato = partes[0].trim();
          servicio = partes[1].trim();
          console.log(`📋 Extraído de localizacion "${servicioInfo.localizacion}": Contrato=${contrato}, Servicio=${servicio}`);
        }
      }
      
      // Si no se pudieron extraer, intentar usar los campos directos o valores por defecto
      const empresa = servicioInfo.empresa || "0001";
      const delegacion = servicioInfo.delegacion || "06";
      contrato = contrato || servicioInfo.contrato;
      servicio = servicio || servicioInfo.servicio;
      
      if (!contrato || !servicio) {
        throw new Error('No se pudo determinar el contrato/servicio del currentService. Verifica que tenga el campo "localizacion"');
      }
      
      // Obtener año y mes del turno
      const fechaTurno = new Date(turnoDetalle.fecha);
      const calendario = fechaTurno.getFullYear().toString();
      const mes = (fechaTurno.getMonth() + 1).toString().padStart(2, '0');
      const dia = fechaTurno.getDate();

      // Preparar datos para Freematica
      const cuadranteData = {
        empresa,
        delegacion,
        calendario,
        mes,
        contrato,
        servicio,
        turnos: [
          {
            numeroTurno: turnoDetalle.numeroTurno || 1,
            personaId: vigilanteId,
            dias: [
              {
                dia: dia,
                horaInicio: turnoDetalle.inicioTurno,
                horaFin: turnoDetalle.finTurno,
                horasTotales: calcularHorasTotales(turnoDetalle.inicioTurno, turnoDetalle.finTurno),
                tarea: "01"
              }
            ]
          }
        ]
      };

      // Actualizar en Freematica
      console.log('Enviando datos a Freematica:', cuadranteData);
      const resultadoFreematica = await actualizarCuadranteFreematica(cuadranteData);
      console.log('Resultado de Freematica:', resultadoFreematica);

      // Verificar si Freematica fue exitoso
      const freematicaExitoso = resultadoFreematica && resultadoFreematica.success;

      // Actualizar también en Firebase
      const turnoRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId, 'turnos', turnoId);
      await updateDoc(turnoRef, {
        vigilante: vigilanteId,
        personaId: vigilanteId,
        nombrePersona: vigilanteData?.nombre || '',
        apellido1: vigilanteData?.apellido1 || '',
        apellido2: vigilanteData?.apellido2 || '',
        sinAsignar: false,
        fechaActualizacion: new Date().toISOString(),
        actualizadoEnFreematica: freematicaExitoso,
        freematicaResultado: resultadoFreematica
      });
      
      console.log(`Vigilante asignado - Firebase: OK, Freematica: ${freematicaExitoso ? 'OK' : 'ERROR'}`);
      
      // Recargar datos
      if (onTurnoActualizado) {
        await onTurnoActualizado();
      }

      return {
        success: true,
        message: `Vigilante asignado exitosamente en Firebase${freematicaExitoso ? ' y Freematica' : ' (error en Freematica)'}`,
        freematicaExitoso
      };

    } catch (error) {
      console.error('Error asignando vigilante a Freematica:', error);
      throw error;
    }
  };


  // Función para obtener el contenido de una celda (mostrar todos los turnos del día)
  const obtenerContenidoCelda = (vigilante, dia) => {
    // Si el día debe ser ocultado, no mostrar turnos
    if (debeOcultarDia(dia)) {
      return null;
    }

    const diaFormateado = dia.toString().padStart(2, '0');
    const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
    const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;

    // Buscar TODOS los turnos del día para este vigilante (excluir los marcados para eliminación)
    const eventosDelDia = processedEvents.filter(event => {
      if (event._pendienteEliminar) return false;
      if (event.fecha !== fechaBuscada) return false;
      return eventoSobreFila(event, vigilante);
    });

    if (eventosDelDia.length === 0) return '';

    // Si hay múltiples turnos, mostrarlos todos
    if (eventosDelDia.length > 1) {
      return (
        <Box display="flex" flexDirection="column" gap={0.5}>
          {eventosDelDia.map((event, index) => {
            const isDragging = draggedTurno?.id === event.id;
            return (
              <Box 
                key={event.id || index} 
                display="flex" 
                flexDirection="column" 
                alignItems="center"
                draggable={true}
                onDragStart={(e) => handleDragStartTurno(e, event, vigilante, dia)}
                onDragEnd={handleDragEnd}
                sx={{
                  position: 'relative',
                  cursor: 'move',
                  opacity: isDragging ? 0.5 : 1,
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    outline: '2px solid #1976d2',
                    borderRadius: '4px'
                  }
                }}
              >
                <button
                  className="delete-turno-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEliminarTurno(event);
                  }}
                  title="Eliminar turno"
                >
                  ✕
                </button>
                {event.sinAsignar ? (
                  <Chip 
                    label="Sin Asignar" 
                    color="warning" 
                    size="small" 
                    variant="outlined"
                    sx={{ pointerEvents: 'none' }}
                  />
                ) : (
                  <>
                    <Typography variant="body2" sx={{ pointerEvents: 'none', fontWeight: 600, lineHeight: 1.2 }}>
                      {event.inicioTurno}
                    </Typography>
                    <Typography variant="body2" sx={{ pointerEvents: 'none', fontWeight: 600, lineHeight: 1.2 }}>
                      {event.finTurno}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ pointerEvents: 'none', fontSize: '0.7rem' }}>
                      {event.turno || 'Sin turno'}
                    </Typography>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      );
    }

    // Si solo hay un turno, mostrarlo como antes pero hacerlo arrastrable
    const event = eventosDelDia[0];
    const isDragging = draggedTurno?.id === event.id;
    
    if (event.sinAsignar) {
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center"
          draggable={true}
          onDragStart={(e) => handleDragStartTurno(e, event, vigilante, dia)}
          onDragEnd={handleDragEnd}
          sx={{
            position: 'relative',
            cursor: 'move',
            opacity: isDragging ? 0.5 : 1,
            transition: 'opacity 0.2s ease',
            '&:hover': {
              outline: '2px solid #1976d2',
              borderRadius: '4px'
            }
          }}
        >
          <button
            className="delete-turno-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleEliminarTurno(event);
            }}
            title="Eliminar turno"
          >
            ✕
          </button>
          <Chip 
            label="Sin Asignar" 
            color="warning" 
            size="small" 
            variant="outlined"
            sx={{ pointerEvents: 'none' }}
          />
          <Typography variant="body2" sx={{ pointerEvents: 'none', fontWeight: 600, lineHeight: 1.2 }}>
            {event.inicioTurno}
          </Typography>
          <Typography variant="body2" sx={{ pointerEvents: 'none', fontWeight: 600, lineHeight: 1.2 }}>
            {event.finTurno}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ pointerEvents: 'none', fontSize: '0.7rem' }}>
            {event.turno || 'Sin turno'}
          </Typography>
        </Box>
      );
    }

    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center"
        draggable={true}
        onDragStart={(e) => handleDragStartTurno(e, event, vigilante, dia)}
        onDragEnd={handleDragEnd}
        sx={{
          position: 'relative',
          cursor: 'move',
          opacity: isDragging ? 0.5 : 1,
          transition: 'opacity 0.2s ease',
          '&:hover': {
            outline: '2px solid #1976d2',
            borderRadius: '4px'
          }
        }}
      >
        <button
          className="delete-turno-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleEliminarTurno(event);
          }}
          title="Eliminar turno"
        >
          ✕
        </button>
        <Typography variant="body2" sx={{ pointerEvents: 'none', fontWeight: 600, lineHeight: 1.2 }}>
          {event.inicioTurno}
        </Typography>
        <Typography variant="body2" sx={{ pointerEvents: 'none', fontWeight: 600, lineHeight: 1.2 }}>
          {event.finTurno}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ pointerEvents: 'none', fontSize: '0.7rem' }}>
          {event.turno || 'Sin turno'}
        </Typography>
      </Box>
    );
  };


  // Función para verificar si hay turnos sin asignar en el mes actual
  const hayTurnosSinAsignar = () => {
    return obtenerTurnosSinAsignar().length > 0;
  };

  // Función para recargar los turnos después de crear un tramo
  const handleTramoCreado = async () => {
    if (!cuadranteId || !currentService) return;
    try {
      // Obtener los turnos actualizados
      const turnos = await obtenerTurnosPorCuadrante(cuadranteId, currentService);
      // Procesar los turnos igual que en useEffect
      const detallesPorId = {};
      for (const turno of turnos) {
        try {
          const turnoDetalle = await obtenerTurnoPorId(cuadranteId, turno.id, currentService);
          if (turnoDetalle) {
            detallesPorId[turno.id] = turnoDetalle;
          } else {
            console.warn(`⚠️ Turno ${turno.id} no existe, será omitido`);
          }
        } catch (error) {
          console.error('Error obteniendo detalles del turno:', error);
        }
      }
      // Función auxiliar para formatear fecha sin problemas de zona horaria
      const formatearFechaLocal = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      
      const processed = turnos.map(turno => ({
        ...turno,
        fecha: detallesPorId[turno.id]?.fecha || (turno.start ? formatearFechaLocal(turno.start) : turno.fecha),
        inicioTurno: detallesPorId[turno.id]?.inicioTurno || (turno.start ? turno.start.getHours().toString().padStart(2, '0') + ':' + turno.start.getMinutes().toString().padStart(2, '0') : turno.inicioTurno),
        finTurno: detallesPorId[turno.id]?.finTurno || (turno.end ? turno.end.getHours().toString().padStart(2, '0') + ':' + turno.end.getMinutes().toString().padStart(2, '0') : turno.finTurno),
        turno: detallesPorId[turno.id]?.turno || '',
        vigilante: detallesPorId[turno.id]?.vigilante,
        sinAsignar: detallesPorId[turno.id]?.sinAsignar || false,
      }));
      setProcessedEvents(processed);
    } catch (error) {
      console.error('Error recargando turnos tras crear tramo:', error);
    }
  };

  // Función auxiliar para actualizar cambios en Freematica
  const actualizarCambiosEnFreematica = async (cambios) => {
    console.log('\n╔══════════════════════════════════════════════════════════════╗');
    console.log('║      🚀 SINCRONIZANDO FIRESTORE → FREEMATICA                ║');
    console.log('╚══════════════════════════════════════════════════════════════╝');
    console.log(`📊 Cambios pendientes a aplicar: ${cambios.length}`);
    console.log('💡 IMPORTANTE: Se enviarán TODOS los turnos con vigilante, no solo los cambios\n');
    
    try {
      // PASO 1: Obtener información del cuadrante (contrato/servicio/delegación)
      console.log('📥 PASO 1: Obteniendo metadatos del cuadrante...');
      const cuadranteRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId);
      const cuadranteDoc = await getDoc(cuadranteRef);
      
      if (!cuadranteDoc.exists()) {
        throw new Error(`No se encontró el cuadrante con ID: ${cuadranteId}`);
      }
      
      const cuadranteData = cuadranteDoc.data();
      console.log('📋 Metadatos del cuadrante:', {
        mesAño: cuadranteData._freematica_mes_año,
        empresa: cuadranteData._freematica_empresa,
        delegacion: cuadranteData._freematica_delegacion,
        contrato: cuadranteData._freematica_contrato,
        servicio: cuadranteData._freematica_servicio
      });
      
      if (!cuadranteData._freematica_empresa) {
        console.warn('⚠️⚠️⚠️ CUADRANTE NO TIENE EMPRESA - SE CONSULTARÁ FREEMATICA ⚠️⚠️⚠️');
      }
      
      // Extraer información del cuadrante
      let contrato = cuadranteData._freematica_contrato;
      let servicio = cuadranteData._freematica_servicio;
      let delegacion = cuadranteData._freematica_delegacion;
      let empresa = cuadranteData._freematica_empresa;
      const mesAño = cuadranteData._freematica_mes_año; // Ej: "2025-11"
      
      // Si el cuadrante no tiene los datos de Freematica, intentar obtenerlos de cualquier turno
      if (!contrato || !servicio || !empresa) {
        console.warn('⚠️ El cuadrante no tiene todos los datos de Freematica');
        console.log('🔍 Intentando obtener de los turnos existentes...');
        
        try {
          // Obtener cualquier turno del cuadrante para extraer los metadatos
          const turnosRef = collection(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId, 'turnos');
          const turnosQuery = query(turnosRef);
          const turnosSnapshot = await getDocs(turnosQuery);
          
          if (!turnosSnapshot.empty) {
            // Usar el primer turno encontrado
            const primerTurno = turnosSnapshot.docs[0].data();
            
            if (!contrato) contrato = primerTurno._freematica_contrato;
            if (!servicio) servicio = primerTurno._freematica_servicio;
            if (!delegacion) delegacion = primerTurno._freematica_delegacion;
            if (!empresa) empresa = primerTurno._freematica_empresa;
            
            console.log(`📋 Extraído de los turnos: Empresa=${empresa}, Delegación=${delegacion}, Contrato=${contrato}, Servicio=${servicio}`);
          } else {
            console.warn('⚠️ No hay turnos en el cuadrante para extraer metadatos');
          }
        } catch (error) {
          console.error('❌ Error obteniendo turnos para extraer metadatos:', error);
        }
      }
      
      // Validar que tenemos los datos necesarios (contrato y servicio son obligatorios)
      if (!contrato || !servicio) {
        console.error('⚠️ ERROR: No se pudo determinar el contrato/servicio');
        console.error('📋 Cuadrante:', cuadranteData);
        console.error('💡 Asegúrate de que el cuadrante se cargó desde Freematica con los campos _freematica_contrato y _freematica_servicio');
        throw new Error('No se pudo determinar el contrato/servicio. El cuadrante no tiene los campos _freematica_contrato/_freematica_servicio');
      }
      
      // Si no tenemos empresa o delegación, obtenerlas del contrato en Freematica
      if (!empresa || !delegacion) {
        console.warn('⚠️ No se encontró empresa y/o delegación en el cuadrante/turno');
        console.log('🔍 Consultando Freematica para obtener datos del contrato...');
        console.log(`📋 Buscando: ${contrato}/${servicio}`);
        
        try {
          const { buscarServiciosPorLocalizacion } = await import('../services/ApiMarcajes');
          const localizacion = `${contrato}/${servicio}`;
          
          // ⚠️ IMPORTANTE: buscarServiciosPorLocalizacion ahora filtra solo por CONTRATO en FIQL
          // y luego filtra por SERVICIO en JavaScript, así que podemos usar cualquier empresa
          // para la autenticación (el header siempre es 0001)
          const resultadoFreematica = await buscarServiciosPorLocalizacion([localizacion]);
          
          if (resultadoFreematica?.data?.items && resultadoFreematica.data.items.length > 0) {
            const contratoInfo = resultadoFreematica.data.items[0];
            
            console.log('📦 Datos obtenidos de Freematica:', {
              empresa: contratoInfo.CTRTS_EMP,
              delegacion: contratoInfo.CTRTS_DELEG,
              contrato: contratoInfo.CTRTS_CTRT,
              servicio: contratoInfo.CTRTS_COD
            });
            
            // Siempre actualizar con los datos de Freematica (source of truth)
            empresa = contratoInfo.CTRTS_EMP;
            delegacion = contratoInfo.CTRTS_DELEG;
            
            console.log(`✅ Datos obtenidos - Empresa: ${empresa}, Delegación: ${delegacion}`);
            
            // Actualizar el cuadrante con los datos obtenidos
            const cuadranteRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId);
            await updateDoc(cuadranteRef, {
              _freematica_empresa: empresa,
              _freematica_delegacion: delegacion
            });
            console.log(`✅ Cuadrante actualizado en Firestore con empresa=${empresa}, delegacion=${delegacion}`);
            
          } else {
            console.warn('⚠️ No se encontró el contrato/servicio en Freematica');
            console.warn(`   Buscado: ${localizacion}`);
            // Solo usar fallback si no se encontró nada
            if (!empresa) {
              console.warn('⚠️ Usando empresa por defecto: 0001');
              empresa = "0001";
            }
            if (!delegacion) {
              console.warn('⚠️ Usando delegación por defecto: 06');
              delegacion = "06";
            }
          }
        } catch (error) {
          console.error('❌ Error consultando Freematica:', error);
          console.error('   Stack:', error.stack);
          // Fallback en caso de error
          if (!empresa) {
            console.warn('⚠️ ERROR - Usando empresa por defecto: 0001');
            empresa = "0001";
          }
          if (!delegacion) {
            console.warn('⚠️ ERROR - Usando delegación por defecto: 06');
            delegacion = "06";
          }
        }
      }
      
      console.log(`✅ Usando: Empresa=${empresa}, Delegación=${delegacion}, Contrato=${contrato}, Servicio=${servicio}`);
      
      // Agrupar turnos por cuadrante (año/mes)
      const turnosPorCuadrante = new Map();
      
      // Procesar todos los cambios y agruparlos
      for (const cambio of cambios) {
        try {
          // Obtener detalles del turno
          const turnoDetalle = await obtenerTurnoPorId(cuadranteId, cambio.turnoId, currentService);
          if (!turnoDetalle) {
            console.warn(`⚠️ No se encontraron detalles para el turno ${cambio.turnoId}`);
            continue;
          }

          // Determinar el vigilante según el tipo de cambio
          let vigilanteId = null;
          
          if (cambio.tipo === 'asignacion') {
            vigilanteId = cambio.vigilanteNuevo.persona;
          } else if (cambio.tipo === 'asignar') {
            vigilanteId = cambio.vigilante;
          } else if (cambio.tipo === 'mover') {
            vigilanteId = cambio.vigilante || turnoDetalle.vigilante;
          } else if (cambio.tipo === 'eliminar') {
            vigilanteId = null;
          }

          // Solo procesar si hay vigilante asignado
          if (!vigilanteId) {
            console.log(`⏭️ Saltando turno ${cambio.turnoId} (sin vigilante)`);
            continue;
          }

          // Determinar la fecha del turno
          let fechaTurno;
          if (cambio.tipo === 'mover' && cambio.fecha) {
            fechaTurno = new Date(cambio.fecha);
          } else {
            fechaTurno = new Date(turnoDetalle.fecha);
          }

          // Obtener año y mes del turno
          const calendario = fechaTurno.getFullYear().toString();
          const mes = (fechaTurno.getMonth() + 1).toString().padStart(2, '0');
          const dia = fechaTurno.getDate();
          
          // Clave única para el cuadrante (empresa/delegacion/año/mes/contrato/servicio)
          const cuadranteKey = `${empresa}_${delegacion}_${calendario}_${mes}_${contrato}_${servicio}`;
          
          // Inicializar array de turnos para este cuadrante si no existe
          if (!turnosPorCuadrante.has(cuadranteKey)) {
            turnosPorCuadrante.set(cuadranteKey, {
              empresa,
              delegacion,
              calendario,
              mes,
              contrato,
              servicio,
              turnos: []
            });
          }
          
          // Agregar el turno al cuadrante correspondiente
          const cuadranteData = turnosPorCuadrante.get(cuadranteKey);
          
          // Buscar si ya existe un turno para esta persona en este día
          let turnoExistente = cuadranteData.turnos.find(
            t => t.personaId === vigilanteId && 
                 t.dias.some(d => d.dia === dia)
          );
          
          if (turnoExistente) {
            // Actualizar día existente
            const diaExistente = turnoExistente.dias.find(d => d.dia === dia);
            if (diaExistente) {
              diaExistente.horaInicio = turnoDetalle.inicioTurno;
              diaExistente.horaFin = turnoDetalle.finTurno;
              diaExistente.horasTotales = calcularHorasTotales(turnoDetalle.inicioTurno, turnoDetalle.finTurno);
              console.log(`🔄 Actualizando turno existente: Persona ${vigilanteId}, Día ${dia}`);
            }
          } else {
            // Agregar nuevo turno
            cuadranteData.turnos.push({
              numeroTurno: turnoDetalle.numeroTurno || 1,
              personaId: vigilanteId,
              dias: [
                {
                  dia: dia,
                  horaInicio: turnoDetalle.inicioTurno,
                  horaFin: turnoDetalle.finTurno,
                  horasTotales: calcularHorasTotales(turnoDetalle.inicioTurno, turnoDetalle.finTurno),
                  tarea: "01"
                }
              ]
            });
            console.log(`➕ Agregando nuevo turno: Persona ${vigilanteId}, Día ${dia}`);
          }
          
        } catch (error) {
          console.error(`❌ Error procesando cambio:`, error);
        }
      }
      
      // ENFOQUE VIEJO ^^^: Procesar cambios individuales
      // ENFOQUE NUEVO vvv: Obtener TODOS los turnos desde Firestore y enviarlos juntos
      
      console.warn('⚠️ NUEVO FLUJO: Ignorando lógica anterior de cambios individuales');
      console.log('\n📥 PASO 2: Obteniendo TODOS los turnos desde FIRESTORE...');
      const { obtenerTurnosExistentesFirestore } = await import('../services/ApiMarcajes');
      
      const [año, mes] = mesAño.split('-'); // "2025-11" -> ["2025", "11"]
      const todosTurnos = await obtenerTurnosExistentesFirestore(cuadranteId, currentService, mes, { empresa, delegacion });
      
      if (todosTurnos.length === 0) {
        console.warn('⚠️ NO HAY TURNOS CON VIGILANTE ASIGNADO EN FIRESTORE');
        console.log('💡 No hay nada que enviar a Freematica');
        return {
          success: true,
          message: 'No hay turnos con vigilante asignado para sincronizar',
          turnosEnviados: 0
        };
      }
      
      console.log(`✅ Turnos obtenidos: ${todosTurnos.length} vigilantes con turnos asignados`);
      
      // PASO 3: Enviar TODOS los turnos a Freematica en un solo PUT
      console.log('\n🚀 PASO 3: Enviando TODOS los turnos a Freematica...');
      const cuadranteDataParaFreematica = {
        empresa,
        delegacion,
        calendario: año,
        mes,
        contrato,
        servicio,
        turnos: todosTurnos // TODOS los turnos con vigilante, ya formateados
      };
      
      console.log(`📦 Datos a enviar:`, {
        empresa,
        delegacion,
        año,
        mes,
        contrato,
        servicio,
        totalVigilantes: todosTurnos.length
      });
      
      // Enviar a Freematica
      const resultadoFreematica = await actualizarCuadranteFreematica(cuadranteDataParaFreematica);
      
      if (resultadoFreematica.success) {
        console.log('\n✅ SINCRONIZACIÓN COMPLETADA');
        console.log(`📈 Turnos enviados: ${resultadoFreematica.turnosEnviados}`);
      } else {
        console.warn('\n⚠️ SINCRONIZACIÓN CON ERRORES');
        console.warn(`📋 Error: ${resultadoFreematica.message}`);
        if (resultadoFreematica.errorTipo) {
          console.warn(`📋 Tipo de error: ${resultadoFreematica.errorTipo}`);
        }
      }
      console.log('╚══════════════════════════════════════════════════════════════╝\n');
      
      return resultadoFreematica;
      
    } catch (error) {
      console.error('❌ ERROR FATAL en actualizarCambiosEnFreematica:', error);
      
      // Retornar errores para todos los cambios
      return cambios.map(cambio => ({
        success: false,
        error: error.message || 'Error fatal',
        turnoId: cambio.turnoId,
        tipo: cambio.tipo
      }));
    }
  };

  // Función para guardar todos los cambios pendientes Y sincronizar con Freematica
  const handleGuardarCuadrante = async () => {
    try {
      setGuardandoCambios(true);
      setResultadoGuardado(null);

      console.log('\n╔══════════════════════════════════════════════════════════════╗');
      console.log('║           💾 GUARDANDO CUADRANTE                             ║');
      console.log('╚══════════════════════════════════════════════════════════════╝');
      
      // PASO 1: Guardar cambios pendientes en Firebase (si los hay)
      if (cambiosPendientes.length > 0) {
        console.log(`📝 PASO 1: Guardando ${cambiosPendientes.length} cambios pendientes en Firebase...`);
        console.log('📋 Cambios pendientes:', cambiosPendientes.map(c => ({
          tipo: c.tipo,
          turnoId: c.turnoId,
          vigilante: c.vigilanteNuevo?.persona || c.vigilante
        })));
        
        // Agrupar cambios para evitar duplicados
        const cambiosAgrupados = agruparCambios(cambiosPendientes);
        console.log(`🔀 Después de agrupar: ${cambiosAgrupados.length} cambios únicos`);
        
        // Validar todos los cambios
        const cambiosInvalidos = [];
        cambiosAgrupados.forEach((cambio, index) => {
          const validacion = validarCambio(cambio);
          if (!validacion.valido) {
            console.error(`❌ Cambio ${index + 1} inválido:`, validacion.error);
            cambiosInvalidos.push({ index, error: validacion.error });
          } else {
            console.log(`✅ Cambio ${index + 1} válido: Turno ${cambio.turnoId} → Vigilante ${cambio.vigilanteNuevo?.persona || cambio.vigilante || 'N/A'}`);
          }
        });

        if (cambiosInvalidos.length > 0) {
          throw new Error(`${cambiosInvalidos.length} cambios inválidos encontrados`);
        }

        // Guardar en Firebase usando batch
        const batch = writeBatch(db);
        console.log('📦 Preparando batch write...');
        
        cambiosAgrupados.forEach((cambio, index) => {
          const turnoRef = doc(db, 'servicios', currentService.id, 'cuadrantes', cuadranteId, 'turnos', cambio.turnoId);
          
          let updateData = {};
          
          if (cambio.tipo === 'asignacion') {
            updateData = {
              vigilante: cambio.vigilanteNuevo.persona,
              vigilanteId: cambio.vigilanteNuevo.id,
              personaId: cambio.vigilanteNuevo.persona,
              nombrePersona: cambio.vigilanteNuevo.nombre,
              apellido1: cambio.vigilanteNuevo.apellidos.split(' ')[0] || '',
              apellido2: cambio.vigilanteNuevo.apellidos.split(' ').slice(1).join(' ') || '',
              sinAsignar: false,
              fechaAsignacion: new Date().toISOString(),
              fechaActualizacion: new Date().toISOString()
            };
            console.log(`   ${index + 1}. 📝 ASIGNACION → Turno: ${cambio.turnoId} | Vigilante: ${updateData.vigilante} | Nombre: ${updateData.nombrePersona} ${updateData.apellido1} ${updateData.apellido2}`);
          } else if (cambio.tipo === 'asignar') {
            // ✅ Guardar TODOS los datos del vigilante
            updateData = {
              vigilante: cambio.vigilante,
              vigilanteId: cambio.vigilanteId || cambio.vigilante,
              personaId: cambio.vigilante,
              nombrePersona: cambio.nombrePersona || '',
              apellido1: cambio.apellido1 || '',
              apellido2: cambio.apellido2 || '',
              sinAsignar: false,
              fechaActualizacion: new Date().toISOString()
            };
            console.log(`   ${index + 1}. 📝 ASIGNAR → Turno: ${cambio.turnoId} | Vigilante: ${updateData.vigilante}`);
          } else if (cambio.tipo === 'mover') {
            // Solo actualizar los campos que realmente cambiaron
            updateData = {
              fechaActualizacion: new Date().toISOString()
            };
            
            // Actualizar fecha solo si cambió
            if (cambio.fecha !== undefined) {
              updateData.fecha = cambio.fecha;
            }
            
            // ✅ Actualizar TODOS los datos del vigilante si cambió
            if (cambio.vigilante !== undefined) {
              updateData.vigilante = cambio.vigilante;
              updateData.vigilanteId = cambio.vigilanteId || cambio.vigilante;
              updateData.personaId = cambio.personaId || cambio.vigilante;
              updateData.sinAsignar = !cambio.vigilante;
              
              // Actualizar nombre y apellidos si están presentes
              if (cambio.nombrePersona !== undefined) {
                updateData.nombrePersona = cambio.nombrePersona;
              }
              if (cambio.apellido1 !== undefined) {
                updateData.apellido1 = cambio.apellido1;
              }
              if (cambio.apellido2 !== undefined) {
                updateData.apellido2 = cambio.apellido2;
              }
            }
            
            const cambiosStr = [];
            if (cambio.fecha) cambiosStr.push(`fecha: ${cambio.fecha}`);
            if (cambio.vigilante !== undefined) {
              const nombre = cambio.nombrePersona ? `${cambio.nombrePersona} ${cambio.apellido1 || ''} ${cambio.apellido2 || ''}`.trim() : cambio.vigilante;
              cambiosStr.push(`vigilante: ${nombre || 'null'}`);
            }
            console.log(`   ${index + 1}. 📝 MOVER → Turno: ${cambio.turnoId} | ${cambiosStr.join(', ') || 'Sin cambios específicos'}`);
          } else if (cambio.tipo === 'eliminar') {
            console.log(`   ${index + 1}. 📝 ELIMINAR → Turno: ${cambio.turnoId} (eliminando documento)`);
            batch.delete(turnoRef);
          }
          
          if (cambio.tipo !== 'eliminar') {
            batch.update(turnoRef, updateData);
          }
        });
        
        console.log('\n🚀 Ejecutando batch.commit()...');
        await batch.commit();
        console.log(`✅ ${cambiosAgrupados.length} cambios guardados exitosamente en Firebase\n`);
      } else {
        console.log('ℹ️  No hay cambios pendientes en la UI');
      }
      
      // PASO 2: Sincronizar TODOS los turnos con Freematica (siempre)
      console.log('\n🔄 PASO 2: Sincronizando con Freematica...');
      console.log('💡 Se enviarán TODOS los turnos con vigilante desde Firestore');
      
      const resultadoFreematica = await actualizarCambiosEnFreematica([]);
      
      // Limpiar cambios pendientes y marcas; recargar grid si hubo eliminaciones
      if (cambiosPendientes.length > 0) {
        const huboEliminaciones = cambiosPendientes.some(c => c.tipo === 'eliminar');
        setCambiosPendientes([]);
        
        const eventosLimpios = processedEvents.map(event => {
          const { _pendienteGuardar, _pendienteEliminar, ...eventoSinMarca } = event;
          return eventoSinMarca;
        });
        setProcessedEvents(eventosLimpios);
        console.log('🧹 Cambios pendientes limpiados');
        if (huboEliminaciones && onTurnoActualizado) {
          await onTurnoActualizado();
        }
      }

      // Preparar mensaje de resultado
      let mensaje = '';
      if (cambiosPendientes.length > 0) {
        mensaje = `✅ ${cambiosPendientes.length} cambios guardados | `;
      }
      
      if (resultadoFreematica.success) {
        mensaje += `Freematica: ${resultadoFreematica.turnosEnviados} turnos sincronizados`;
      } else {
        mensaje += `⚠️ Error sincronizando con Freematica`;
        
        // Si hay detalles del error en el campo 'data', formatearlos
        if (resultadoFreematica.data) {
          const dataStr = resultadoFreematica.data;
          // Dividir por corchetes y formatear
          const mensajes = dataStr
            .split('[')
            .filter(m => m.trim())
            .map(m => m.replace(/\]$/, '').trim());
          
          // Agregar los mensajes formateados
          mensaje += ':\n\n' + mensajes.map((m, idx) => `${idx + 1}. ${m}`).join('\n');
        }
      }

      console.log('📊 Resultado final:', mensaje);
      console.log('╚══════════════════════════════════════════════════════════════╝\n');

      setResultadoGuardado({
        success: resultadoFreematica.success,
        message: mensaje
      });

      // Limpiar mensaje después de más tiempo si hay error (para que el usuario pueda leer)
      const timeout = resultadoFreematica.success ? 5000 : 15000;
      setTimeout(() => setResultadoGuardado(null), timeout);

    } catch (error) {
      console.error('Error guardando cambios:', error);
      setResultadoGuardado({
        success: false,
        message: `❌ Error: ${error.message}`
      });
      
      // Limpiar mensaje de error después de 10 segundos
      setTimeout(() => setResultadoGuardado(null), 10000);
    } finally {
      setGuardandoCambios(false);
    }
  };

  // Función para descartar todos los cambios pendientes
  const handleDescartarCambios = () => {
    setCambiosPendientes([]);
    // Recargar los eventos originales
    if (onTurnoActualizado) {
      onTurnoActualizado();
    }
    setResultadoGuardado({
      success: true,
      message: 'Cambios descartados'
    });
    setTimeout(() => setResultadoGuardado(null), 3000);
  };

  // Funciones para drag and drop
  // Nueva función para arrastrar un turno específico (individual)
  const handleDragStartTurno = (e, turno, vigilante, dia) => {
    e.stopPropagation(); // Evitar propagación al td padre
    setDraggedTurno({
      turno,
      vigilanteOrigen: vigilante,
      diaOrigen: dia
    });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  // Función antigua (mantener por compatibilidad)
  const handleDragStart = (e, vigilante, dia) => {
    const diaFormateado = dia.toString().padStart(2, '0');
    const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
    const fechaBuscada = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;

    const rowSinAsignar = uniqueVigilantes.find(v => v.sinAsignar && v.nombre === vigilante);
    const turno = rowSinAsignar
      ? processedEvents.find(event => {
          if (event._pendienteEliminar || event.fecha !== fechaBuscada) return false;
          return eventoSobreFila(event, rowSinAsignar);
        })
      : processedEvents.find(event => {
          if (event._pendienteEliminar) return false;
          if (event.fecha !== fechaBuscada) return false;
          return eventoSobreFila(event, vigilante);
        });

    if (turno) {
      setDraggedTurno({
        turno,
        vigilanteOrigen: vigilante,
        diaOrigen: dia
      });
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', e.target);
    }
  };

  const handleDragOver = (e, vigilante, dia) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell({ vigilante, dia });
  };

  const handleDragLeave = () => {
    setDragOverCell(null);
  };

  const handleDrop = (e, vigilanteDestino, diaDestino) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedTurno) return;

    const { turno, vigilanteOrigen, diaOrigen } = draggedTurno;
    const vigOrigenNombre = typeof vigilanteOrigen === 'object' ? vigilanteOrigen?.nombre : vigilanteOrigen;

    // Si se suelta en la misma celda, no hacer nada
    if (vigOrigenNombre === vigilanteDestino && diaOrigen === diaDestino) {
      setDraggedTurno(null);
      setDragOverCell(null);
      return;
    }

    // Preparar los cambios
    const cambios = {};

    // Si cambia el día
    if (diaOrigen !== diaDestino) {
      const diaNumero = typeof diaDestino === 'string' ? parseInt(diaDestino.split(' ')[0]) : diaDestino;
      const diaFormateado = diaNumero.toString().padStart(2, '0');
      const mesFormateado = (mesSeleccionado.getMonth() + 1).toString().padStart(2, '0');
      cambios.fecha = `${mesSeleccionado.getFullYear()}-${mesFormateado}-${diaFormateado}`;
    }

    // Si cambia el vigilante (incl. destino "Sin asignar (N)")
    const destinoEsSinAsignar = vigilanteDestino === 'Sin Asignar' || uniqueVigilantes.find(v => v.nombre === vigilanteDestino)?.sinAsignar;
    if (vigOrigenNombre !== vigilanteDestino) {
      if (destinoEsSinAsignar) {
        cambios.vigilante = null;
        cambios.vigilanteId = null;
        cambios.personaId = null;
        cambios.nombrePersona = null;
        cambios.apellido1 = null;
        cambios.apellido2 = null;
        cambios.nombreVigilante = null;
      } else {
        // Buscar el vigilante en la lista de únicos
        const vigilanteInfo = uniqueVigilantes.find(v => v.nombre === vigilanteDestino);
        if (vigilanteInfo) {
          // ✅ Guardar TODOS los datos del vigilante consistentemente
          cambios.vigilante = vigilanteInfo.persona; // Código de persona
          cambios.vigilanteId = vigilanteInfo.id || vigilanteInfo.persona; // ID del vigilante
          cambios.personaId = vigilanteInfo.persona; // Código de persona (campo adicional para consistencia)
          cambios.nombrePersona = vigilanteInfo.nombrePersona || vigilanteInfo.nombre || '';
          cambios.apellido1 = vigilanteInfo.apellido1 || '';
          cambios.apellido2 = vigilanteInfo.apellido2 || '';
          cambios.nombreVigilante = vigilanteDestino;
        }
      }
    }

    // Crear el cambio de movimiento
    const cambioMovimiento = crearCambioMovimiento(turno.id, cambios);

    // Agregar a cambios pendientes
    setCambiosPendientes([...cambiosPendientes, cambioMovimiento]);

    // Aplicar cambio localmente
    const eventosActualizados = aplicarCambioMovimiento(processedEvents, cambioMovimiento);
    setProcessedEvents(eventosActualizados);

    // Limpiar estados de drag
    setDraggedTurno(null);
    setDragOverCell(null);
  };

  const handleDragEnd = () => {
    setDraggedTurno(null);
    setDragOverCell(null);
  };

  // Renderizado del componente
  return (
    <div className="grid-view" style={{ 
      transform: `scale(${zoomLevel / 100})`, 
      transformOrigin: 'top left',
      width: `${10000 / zoomLevel}%`
    }}>
      {/* Indicador de carga */}
      {cargandoTurnos && (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          p={4}
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 2,
            margin: 2
          }}
        >
          <CircularProgress size={40} sx={{ color: '#0d47a1', mb: 2 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            Cargando turnos...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Obteniendo detalles de los turnos del cuadrante
          </Typography>
        </Box>
      )}

      {/* Selector de mes removido - el mes se carga desde los datos */}

      {/* Información del Cuadrante */}
      {(infoCuadrante.empresa || infoCuadrante.delegacion || infoCuadrante.contrato) && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3, 
            padding: '16px 20px',
            backgroundColor: '#e3f2fd',
            borderLeft: '4px solid #1976d2',
            marginBottom: '16px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600, 
              color: '#1565c0',
              marginRight: '8px'
            }}
          >
            📋 Información del Cuadrante:
          </Typography>
          
          {infoCuadrante.contrato && (
            <Chip 
              label={`Contrato: ${infoCuadrante.contrato}`}
              sx={{ 
                backgroundColor: '#fff',
                fontWeight: 500,
                fontSize: '0.875rem',
                border: '1px solid #90caf9'
              }}
            />
          )}
          
          {infoCuadrante.servicio && (
            <Chip 
              label={`Servicio: ${infoCuadrante.servicio}`}
              sx={{ 
                backgroundColor: '#fff',
                fontWeight: 500,
                fontSize: '0.875rem',
                border: '1px solid #90caf9'
              }}
            />
          )}
          
          {infoCuadrante.empresa && (
            <Chip 
              label={`Empresa: ${infoCuadrante.empresa}`}
              sx={{ 
                backgroundColor: '#fff',
                fontWeight: 500,
                fontSize: '0.875rem',
                border: '1px solid #90caf9'
              }}
            />
          )}
          
          {infoCuadrante.delegacion && (
            <Chip 
              label={`Delegación: ${infoCuadrante.delegacion}`}
              sx={{ 
                backgroundColor: '#fff',
                fontWeight: 500,
                fontSize: '0.875rem',
                border: '1px solid #90caf9'
              }}
            />
          )}
        </Box>
      )}

      {/* Leyenda de filtros activos */}
      {tiposFestivosOcultar.length > 0 && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            padding: '12px 16px',
            backgroundColor: '#fff3e0',
            borderLeft: '4px solid #ff6b6b',
            marginBottom: '16px',
            borderRadius: '4px'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#c92a2a' }}>
            🚫 Turnos ocultos en:
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {(() => {
              const labels = [];
              if (tiposFestivosOcultar.includes('nacionales')) labels.push('🇪🇸 Festivos Nacionales');
              if (tiposFestivosOcultar.includes('domingos')) labels.push('📅 Domingos');
              if (tiposFestivosOcultar.includes('sabados')) labels.push('📅 Sábados');
              return labels.join(', ');
            })()}
          </Typography>
          <Box 
            sx={{ 
              width: '30px', 
              height: '30px', 
              backgroundColor: '#f5f5f5', 
              border: '2px dashed #999',
              borderRadius: '4px',
              marginLeft: 'auto',
              opacity: 0.4
            }} 
          />
        </Box>
      )}

      {/* Botón para asignar tramo completo - REMOVIDO DE AQUÍ */}
      
      <table>
        <thead>
          <tr>
            <th>Usuarios</th>
            <th>Turno</th>
            {daysOfMonth.map((day, index) => (
              <th key={index}>{typeof day === 'object' ? day.label : day}</th>
            ))}
            <th style={{ 
              backgroundColor: '#0d47a1', 
              color: 'white',
              minWidth: '120px'
            }}>
              Total Horas
            </th>
            <th style={{ 
              backgroundColor: '#2e7d32', 
              color: 'white',
              minWidth: '120px'
            }}>
              Horas Disponibles
            </th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {uniqueVigilantes.map((vigilante, vIndex) => (
            <tr key={vIndex}>
              <td>
                {vigilante.sinAsignar ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      {vigilante.nombre}
                    </Typography>
                    {(vigilante.eventIds?.length > 0 || vigilante.eventId) && (() => {
                      const ids = vigilante.eventIds?.length ? vigilante.eventIds : [vigilante.eventId];
                      const primerEvento = processedEvents.find(e => e.id === ids[0] && e.sinAsignar);
                      return primerEvento ? (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            setTurnoParaAsignar(primerEvento);
                            setAsignarFilaCompleta(ids.length > 1 ? { eventIds: ids } : null);
                            setMostrarAsignarVigilante(true);
                          }}
                          sx={{
                            backgroundColor: '#0d47a1',
                            '&:hover': { backgroundColor: '#0a3d8f' }
                          }}
                        >
                          Asignar {ids.length > 1 ? `(${ids.length} turnos)` : ''}
                        </Button>
                      ) : null;
                    })()}
                  </Box>
                ) : (
                  formatearVigilante(vigilante)
                )}
              </td>
              <td>{vigilante.turno}</td>
              {daysOfMonth.map((day, dIndex) => {
                const diaNum = typeof day === 'object' ? day.dia : dIndex + 1;
                const diaOculto = debeOcultarDia(diaNum);
                const hasTurno = !diaOculto && tieneTurno(vigilante, diaNum);
                const isDragOver = dragOverCell?.vigilante === (vigilante?.nombre ?? vigilante) && dragOverCell?.dia === diaNum;
                
                return (
                  <td
                    key={`${vIndex}-${dIndex}`}
                    onDragOver={(e) => handleDragOver(e, typeof vigilante === 'object' ? vigilante.nombre : vigilante, dIndex + 1)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, typeof vigilante === 'object' ? vigilante.nombre : vigilante, dIndex + 1)}
                    onClick={() => {
                      setCeldaSeleccionada({ vigilante, dia: diaNum });
                      if (!diaOculto) handleEditarCasilla(vigilante, dIndex + 1);
                    }}
                    onContextMenu={(e) => !diaOculto && handleContextMenuCelda(e, vigilante, diaNum)}
                    className={`${hasTurno ? 'has-turno' : ''} ${diaOculto ? 'dia-oculto' : ''}`}
                    style={{ 
                      cursor: diaOculto ? 'not-allowed' : (hasTurno ? 'default' : 'default'),
                      backgroundColor: isDragOver ? '#fff3cd' : (diaOculto ? '#f5f5f5' : ''),
                      border: isDragOver ? '2px dashed #f57c00' : '',
                      transition: 'all 0.2s ease',
                      padding: '8px',
                      opacity: diaOculto ? 0.4 : 1
                    }}
                    title={diaOculto ? 'Turnos ocultos (filtro activo)' : 'Clic: editar. Clic derecho: copiar/pegar. Ctrl+C / Ctrl+V'}
                  >
                    {!diaOculto && obtenerContenidoCelda(vigilante, diaNum)}
                  </td>
                );
              })}
              <td style={{ 
                fontWeight: 'bold', 
                fontSize: '0.8rem',
                backgroundColor: '#e7f5ff',
                textAlign: 'center'
              }}>
                {calcularTotalHoras(vigilante)}h
              </td>
              <td style={{ 
                fontWeight: 'bold', 
                fontSize: '0.7rem',
                backgroundColor: (() => {
                  const horasInfo = calcularHorasDisponibles(vigilante);
                  return horasInfo.excedido ? '#ffebee' : '#e8f5e9';
                })(),
                color: (() => {
                  const horasInfo = calcularHorasDisponibles(vigilante);
                  return horasInfo.excedido ? '#c62828' : '#2e7d32';
                })(),
                textAlign: 'center',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                maxWidth: '85px',
                lineHeight: 1.2
              }}>
                {(() => {
                  const horasInfo = calcularHorasDisponibles(vigilante);
                  return horasInfo.excedido 
                    ? `${Math.abs(horasInfo.disponibles)}h excedidas`
                    : `${horasInfo.disponibles}h`;
                })()}
              </td>
              <td>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => handleDuplicarFila(vigilante)}
                    disabled={duplicandoFila || !processedEvents.some((e) => !e._pendienteEliminar && eventoSobreFila(e, vigilante))}
                  >
                    {duplicandoFila ? '...' : 'Duplicar fila (sin asignar)'}
                  </Button>
                  {!vigilante.sinAsignar && (
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      size="small"
                      onClick={() => handleCambiarUsuarioFila(vigilante)}
                    >
                      Cambiar usuario
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="error" 
                    size="small"
                    onClick={() => handleEliminarFila(vigilante)}
                  >
                    Eliminar
                  </Button>
                </Box>
              </td>
            </tr>
          ))}
          
          {/* Fila de totales por día */}
          <tr style={{ 
            backgroundColor: '#f0f4f8', 
            borderTop: '3px solid #0d47a1',
            fontWeight: 'bold'
          }}>
            <td colSpan="2" style={{ 
              textAlign: 'center',
              backgroundColor: '#0d47a1',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}>
              Total por Día
            </td>
            {daysOfMonth.map((day, index) => {
              const diaNum = typeof day === 'object' ? day.dia : index + 1;
              const totalDia = calcularTotalHorasPorDia(diaNum);
              return (
                <td key={index} style={{
                  backgroundColor: totalDia ? '#e3f2fd' : 'transparent',
                  padding: '12px 8px',
                  textAlign: 'center',
                  fontSize: '0.85rem',
                  lineHeight: '1.3'
                }}>
                  {totalDia ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ 
                        fontSize: '1rem', 
                        fontWeight: 'bold',
                        color: '#0d47a1'
                      }}>
                        {totalDia.total}h
                      </div>
                      <div style={{ 
                        fontSize: '0.75rem',
                        color: '#546e7a',
                        borderTop: '1px solid #b3d9ff',
                        paddingTop: '4px'
                      }}>
                        {totalDia.inicio} - {totalDia.fin}
                      </div>
                      <div style={{ 
                        fontSize: '0.7rem',
                        color: '#999',
                        marginTop: '2px'
                      }}>
                        ({processedEvents.filter(e => !e._pendienteEliminar && e.fecha === `${mesSeleccionado.getFullYear()}-${(mesSeleccionado.getMonth() + 1).toString().padStart(2, '0')}-${diaNum.toString().padStart(2, '0')}`).length} turnos)
                      </div>
                    </div>
                  ) : (
                    '-'
                  )}
                </td>
              );
            })}
            <td colSpan="3" style={{ 
              backgroundColor: '#0d47a1',
              color: 'white'
            }}>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Menú contextual: copiar / pegar turno */}
      <Menu
        open={contextMenuCelda !== null}
        onClose={() => setContextMenuCelda(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenuCelda ? { top: contextMenuCelda.mouseY, left: contextMenuCelda.mouseX } : undefined
        }
        MenuListProps={{ dense: true }}
      >
        {contextMenuCelda && getEventosEnCelda(contextMenuCelda.vigilante, contextMenuCelda.dia).length > 0 && (
          <MenuItem onClick={handleCopiarTurno}>Copiar turno (Ctrl+C)</MenuItem>
        )}
        {copiedTurnoData && (
          <MenuItem onClick={handlePegarTurno} disabled={pegandoTurno}>
            {pegandoTurno ? 'Pegando...' : 'Pegar turno (Ctrl+V)'}
          </MenuItem>
        )}
        {contextMenuCelda && !copiedTurnoData && getEventosEnCelda(contextMenuCelda.vigilante, contextMenuCelda.dia).length === 0 && (
          <MenuItem disabled>Selecciona una celda con turno para copiar; luego pega aquí</MenuItem>
        )}
      </Menu>

      {/* Modales - renderizados en document.body para que se superpongan y no se corten */}
      <Dialog open={selectorTurnoAbierto} onClose={() => { setSelectorTurnoAbierto(false); setEventosParaElegir([]); }} maxWidth="xs" fullWidth>
        <DialogTitle>Varios turnos en esta celda</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elija el turno que desea actualizar:
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {eventosParaElegir.map((event) => (
              <Button
                key={event.id}
                variant="outlined"
                fullWidth
                onClick={() => {
                  if (event.sinAsignar) {
                    setTurnoParaAsignar(event);
                    setMostrarAsignarVigilante(true);
                  } else {
                    setTurnoSeleccionado(event.id);
                  }
                  setSelectorTurnoAbierto(false);
                  setEventosParaElegir([]);
                }}
              >
                {event.sinAsignar ? 'Sin asignar' : (event.title || 'Turno')}: {event.inicioTurno} - {event.finTurno}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSelectorTurnoAbierto(false); setEventosParaElegir([]); }}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {turnoSeleccionado && createPortal(
        <div className="modal">
          <div className="modal-content">
            <ActualizarTurno
              cuadranteId={cuadranteId}
              turnoId={turnoSeleccionado}
              currentService={currentService}
              onUpdateComplete={() => {
                setTurnoSeleccionado(null);
                if (typeof onTurnoActualizado === 'function') onTurnoActualizado();
              }}
              servicioId={servicioId}
              onClose={() => setTurnoSeleccionado(null)}
            />
          </div>
        </div>,
        document.body
      )}

      {servicioId && (
        <Box>
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <Button variant="contained" color="primary" onClick={handleMostrarFormularioTramo}>
              Crear Tramo
            </Button>
            <Button 
              variant="contained" 
              onClick={() => setMostrarAgregarVigilante(true)}
              sx={{ 
                backgroundColor: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#1b5e20'
                }
              }}
            >
              Agregar Vigilante
            </Button>
            
            {/* Botón Guardar Cuadrante - Sincroniza con Freematica */}
            <Badge 
              badgeContent={contarCambiosPendientes(cambiosPendientes).total} 
              color="error"
              sx={{ '& .MuiBadge-badge': { fontSize: '0.9rem', padding: '0 6px' } }}
            >
              <Button 
                variant="contained" 
                onClick={handleGuardarCuadrante}
                disabled={guardandoCambios}
                sx={{ 
                  backgroundColor: cambiosPendientes.length > 0 ? '#f57c00' : '#1976d2',
                  '&:hover': {
                    backgroundColor: cambiosPendientes.length > 0 ? '#ef6c00' : '#1565c0'
                  },
                  minWidth: '180px'
                }}
                title={cambiosPendientes.length > 0 
                  ? `Guardar ${cambiosPendientes.length} cambios y sincronizar con Freematica` 
                  : 'Sincronizar todos los turnos con Freematica'
                }
              >
                {guardandoCambios ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>Guardando...</span>
                  </Box>
                ) : cambiosPendientes.length > 0 ? (
                  '💾 Guardar y Sincronizar'
                ) : (
                  '🔄 Sincronizar Freematica'
                )}
              </Button>
            </Badge>

            {/* Botón Descartar Cambios */}
            {cambiosPendientes.length > 0 && (
              <Button 
                variant="outlined" 
                color="error"
                onClick={handleDescartarCambios}
                disabled={guardandoCambios}
              >
                Descartar Cambios
              </Button>
            )}
            
            {/* Controles de Zoom */}
            <Box display="flex" alignItems="center" gap={1} ml="auto">
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
                disabled={zoomLevel <= 50}
              >
                −
              </Button>
              <Typography variant="body2" sx={{ minWidth: '60px', textAlign: 'center' }}>
                {zoomLevel}%
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}
                disabled={zoomLevel >= 150}
              >
                +
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setZoomLevel(100)}
              >
                Reset
              </Button>
            </Box>
          </Box>

          {/* Mensaje de resultado */}
          {resultadoGuardado && (
            <Alert 
              severity={resultadoGuardado.success ? "success" : "error"} 
              sx={{ 
                mb: 2, 
                whiteSpace: 'pre-line', // Preservar saltos de línea
                '& .MuiAlert-message': {
                  width: '100%',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }
              }}
              onClose={() => setResultadoGuardado(null)}
            >
              {resultadoGuardado.message}
            </Alert>
          )}
        </Box>
      )}

      {mostrarFormularioTramo && createPortal(
        <div className="modal">
          <div className="modal-content">
            <CreateTramo
              open={true}
              onClose={() => setMostrarFormularioTramo(false)}
              cuadranteId={cuadranteId}
              currentService={currentService}
              servicioId={servicioId}
              onTramoCreado={handleTramoCreado}
            />
          </div>
        </div>,
        document.body
      )}

      {filaAEliminar !== null && createPortal(
        <div className="modal">
          <div className="modal-content">
            <p>¿Estás seguro de que deseas eliminar esta fila?</p>
            <div className="modal-buttons">
              <Button variant="contained" color="primary" onClick={handleConfirmarEliminarFila}>
                Confirmar
              </Button>
              <Button variant="contained" onClick={() => setFilaAEliminar(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Dialog Cambiar usuario de la fila (sin eliminar turnos) */}
      {mostrarCambiarUsuarioFila && filaParaCambiarUsuario && (
        <Dialog 
          open={mostrarCambiarUsuarioFila} 
          onClose={() => {
            setMostrarCambiarUsuarioFila(false);
            setFilaParaCambiarUsuario(null);
            setBusquedaCambiarUsuarioFila('');
            setVigilanteSeleccionadoCambiarFila('');
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>Cambiar usuario de la fila</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Reasignar todos los turnos de <strong>{filaParaCambiarUsuario.vigilanteNombre}</strong> al nuevo usuario. Los turnos se mantienen; solo cambia el trabajador asignado.
            </Typography>
            <TextField
              fullWidth
              margin="normal"
              label="Buscar usuario"
              variant="outlined"
              value={busquedaCambiarUsuarioFila}
              onChange={(e) => setBusquedaCambiarUsuarioFila(e.target.value)}
              placeholder="Nombre o nº empleado..."
              disabled={loadingVigilantes || aplicandoCambioUsuarioFila}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="cambiar-usuario-fila-label">Nuevo usuario</InputLabel>
              <Select
                labelId="cambiar-usuario-fila-label"
                value={vigilanteSeleccionadoCambiarFila}
                label="Nuevo usuario"
                onChange={(e) => setVigilanteSeleccionadoCambiarFila(e.target.value)}
                disabled={loadingVigilantes || aplicandoCambioUsuarioFila}
              >
                {loadingVigilantes ? (
                  <MenuItem value=""><Box display="flex" alignItems="center" gap={1}><CircularProgress size={20} /><span>Cargando...</span></Box></MenuItem>
                ) : (
                  vigilantes
                    .filter(v => {
                      if (!busquedaCambiarUsuarioFila.trim()) return true;
                      const n = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
                      const term = n(busquedaCambiarUsuarioFila);
                      return n(formatearVigilante(v)).includes(term) || n(v.persona || '').includes(term);
                    })
                    .map(v => (
                      <MenuItem key={v.id} value={v.id}>{formatearVigilante(v)}</MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setMostrarCambiarUsuarioFila(false); setFilaParaCambiarUsuario(null); setBusquedaCambiarUsuarioFila(''); setVigilanteSeleccionadoCambiarFila(''); }} disabled={aplicandoCambioUsuarioFila}>
              Cancelar
            </Button>
            <Button color="primary" variant="contained" onClick={handleConfirmarCambiarUsuarioFila} disabled={aplicandoCambioUsuarioFila || !vigilanteSeleccionadoCambiarFila || loadingVigilantes}>
              {aplicandoCambioUsuarioFila ? <Box display="flex" alignItems="center" gap={1}><CircularProgress size={20} /><span>Aplicando...</span></Box> : 'Cambiar usuario'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {mostrarAsignarVigilante && turnoParaAsignar && (
        <AsignarVigilante
          open={mostrarAsignarVigilante}
          cuadranteId={cuadranteId}
          turnoId={turnoParaAsignar.id}
          currentService={currentService}
          servicioId={servicioId}
          onVigilanteAsignado={handleVigilanteAsignado}
          onAsignarAFreematica={handleAsignarVigilanteAFreematica}
          onAsignacionPendiente={handleAsignacionPendiente}
          empresaCuadrante={infoCuadrante.empresa}
          delegacionCuadrante={infoCuadrante.delegacion}
          onClose={() => {
            setMostrarAsignarVigilante(false);
            setTurnoParaAsignar(null);
            setAsignarFilaCompleta(null);
          }}
        />
      )}

      {/* Modal para asignar vigilante a todo el tramo */}
      {mostrarAsignarTramoCompleto && (
        <Dialog open={mostrarAsignarTramoCompleto} onClose={() => {
          setMostrarAsignarTramoCompleto(false);
          setBusquedaVigilante('');
          setVigilanteSeleccionado('');
        }} maxWidth="sm" fullWidth>
          <DialogTitle>Asignar Usuario a Tramo Completo</DialogTitle>
          <DialogContent>
            {errorAsignacion && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorAsignacion}
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" mb={2}>
              Se asignarán {obtenerTurnosSinAsignar().length} turnos sin asignar del mes actual al usuario seleccionado.
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              <strong>Nota:</strong> Se muestran todos los usuarios disponibles del servicio.
            </Typography>

            {/* Campo de búsqueda */}
            <TextField
              fullWidth
              margin="normal"
              label="Buscar usuario"
              variant="outlined"
              value={busquedaVigilante}
              onChange={(e) => setBusquedaVigilante(e.target.value)}
              placeholder="Escriba para buscar usuarios..."
              disabled={loadingVigilantes || asignandoTramo}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="vigilante-label">Usuario</InputLabel>
              <Select
                labelId="vigilante-label"
                value={vigilanteSeleccionado}
                label="Usuario"
                onChange={(e) => setVigilanteSeleccionado(e.target.value)}
                disabled={loadingVigilantes || asignandoTramo}
              >
                {loadingVigilantes ? (
                  <MenuItem value="">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      <span>Cargando usuarios...</span>
                    </Box>
                  </MenuItem>
                ) : vigilantes.length > 0 ? (
                  vigilantes
                    .filter(vigilante => {
                      if (!busquedaVigilante) return true;
                      
                      // Normalizar: minúsculas, sin acentos, sin espacios extras
                      const normalizar = (texto) => {
                        return texto
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
                          .replace(/\s+/g, ' ') // Normalizar espacios
                          .trim();
                      };
                      
                      const searchTerm = normalizar(busquedaVigilante);
                      const vigilanteText = normalizar(formatearVigilante(vigilante));
                      const persona = normalizar(vigilante.persona || '');
                      const nif = normalizar(vigilante.nif || '');
                      
                      return vigilanteText.includes(searchTerm) || 
                             persona.includes(searchTerm) ||
                             nif.includes(searchTerm);
                    })
                    .map(vigilante => (
                      <MenuItem key={vigilante.id} value={vigilante.id}>
                        {formatearVigilante(vigilante)}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem value="" disabled>
                    No hay usuarios disponibles
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setMostrarAsignarTramoCompleto(false);
                setBusquedaVigilante('');
                setVigilanteSeleccionado('');
              }} 
              disabled={asignandoTramo}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleAsignarTramoCompleto} 
              color="primary" 
              variant="contained" 
              disabled={asignandoTramo || !vigilanteSeleccionado || loadingVigilantes}
              sx={{ 
                backgroundColor: '#0d47a1',
                '&:hover': {
                  backgroundColor: '#0a3d8f'
                }
              }}
            >
              {asignandoTramo ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <span>Asignando...</span>
                </Box>
              ) : (
                'Asignar Tramo Completo'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal para agregar vigilante sin turnos */}
      {mostrarAgregarVigilante && (
        <Dialog 
          open={mostrarAgregarVigilante} 
          onClose={() => {
            setMostrarAgregarVigilante(false);
            setBusquedaVigilante('');
            setVigilanteSeleccionado('');
            setErrorAsignacion('');
          }} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>Agregar Vigilante Sin Turnos</DialogTitle>
          <DialogContent>
            {errorAsignacion && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorAsignacion}
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" mb={2}>
              Agregue un vigilante a la cuadrícula sin asignar turnos. Podrá asignar turnos manualmente después.
            </Typography>

            {/* Campo de búsqueda */}
            <TextField
              fullWidth
              margin="normal"
              label="Buscar usuario"
              variant="outlined"
              value={busquedaVigilante}
              onChange={(e) => setBusquedaVigilante(e.target.value)}
              placeholder="Escriba para buscar usuarios..."
              disabled={loadingVigilantes}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="vigilante-agregar-label">Usuario</InputLabel>
              <Select
                labelId="vigilante-agregar-label"
                value={vigilanteSeleccionado}
                label="Usuario"
                onChange={(e) => setVigilanteSeleccionado(e.target.value)}
                disabled={loadingVigilantes}
              >
                {loadingVigilantes ? (
                  <MenuItem value="">
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      <span>Cargando usuarios...</span>
                    </Box>
                  </MenuItem>
                ) : vigilantes.length > 0 ? (
                  vigilantes
                    .filter(vigilante => {
                      if (!busquedaVigilante) return true;
                      
                      // Normalizar: minúsculas, sin acentos, sin espacios extras
                      const normalizar = (texto) => {
                        return texto
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
                          .replace(/\s+/g, ' ') // Normalizar espacios
                          .trim();
                      };
                      
                      const searchTerm = normalizar(busquedaVigilante);
                      const vigilanteText = normalizar(formatearVigilante(vigilante));
                      const persona = normalizar(vigilante.persona || '');
                      const nif = normalizar(vigilante.nif || '');
                      
                      return vigilanteText.includes(searchTerm) || 
                             persona.includes(searchTerm) ||
                             nif.includes(searchTerm);
                    })
                    .map(vigilante => (
                      <MenuItem key={vigilante.id} value={vigilante.id}>
                        {formatearVigilante(vigilante)}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem value="" disabled>
                    No hay usuarios disponibles
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setMostrarAgregarVigilante(false);
                setBusquedaVigilante('');
                setVigilanteSeleccionado('');
                setErrorAsignacion('');
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleAgregarVigilanteSinTurnos} 
              color="primary" 
              variant="contained" 
              disabled={!vigilanteSeleccionado || loadingVigilantes}
              sx={{ 
                backgroundColor: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#1b5e20'
                }
              }}
            >
              Agregar Vigilante
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default GridView;